/**
 * PlanChangeFlow — every purchase or plan change goes through here, so the
 * host sees exactly what happens BEFORE anything is charged or switched off:
 *
 *   1. Ask create-checkout for a preview ({ preview: true }, nothing changes):
 *        now        — "Danas se naplaćuje 12,34 €", next renewal + new price
 *        period_end — "Od 12. 3. 2027. prelaziš na Starter", nothing today
 *        checkout   — no live subscription: the price, then Stripe Checkout
 *                     (warning when a gift/beta/trial is replaced)
 *      Business refusals (EA sold out, deletion pending, card failed, payment
 *      pending…) show up here already, before any click on "pay".
 *   2. TOO_MANY_UNITS (the new plan is smaller than the active units): the
 *      unit list appears INSIDE the confirmation, with what the server says
 *      it will do (amount / date / renewal back on / pending change dropped).
 *      Units are switched off only when the host confirms; if the following
 *      change fails they are switched back on. Cancelling never touches a
 *      unit. Switched-off units are remembered for the session so they can be
 *      switched back on if the host then cancels Checkout / the change.
 *      Renewal off (REACTIVATE_FIRST, or TOO_MANY_UNITS with reactivate_first):
 *      the host turns renewal on FIRST, then the flow previews again — the
 *      unit list never appears before that.
 *   3. Confirm. A "now" change sends the preview's proration_date back (the
 *      charge equals the amount shown); a preview older than ~50 min is
 *      recalculated and shown again first. Results: updated / scheduled /
 *      change_cancelled / requires_action (3-D Secure link) / readable error.
 *      No answer within ~45 s (TIMEOUT), or the connection / server broke
 *      after the request was sent (NETWORK / RELAY / SERVER): the outcome is
 *      UNKNOWN — a closable error says to check Billing; units switched off
 *      for it stay off and are never switched back on or offered back (the
 *      server may still finish the change). The units offer only follows an
 *      outcome that is known: a refused change, REACTIVATE_FIRST, a cancelled
 *      re-preview, a cancelled scheduled change, Back from Checkout.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useUser } from '@clerk/clerk-react';
import { AlertTriangle, CheckCircle, CreditCard, ExternalLink, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui';
import { useCreateCheckout, useCustomerPortal, subscriptionKeys, type BillingInfo } from '@/hooks/useSubscription';
import { useReactivateSubscription } from '@/hooks/useReactivateSubscription';
import {
  PRICING_TIERS,
  type ChangePreview,
  type CheckoutResult,
  type PartialPreview,
  type PricingTierId,
  syncMyBilling,
} from '@/lib/stripe';
import {
  effectiveHasAccess,
  formatBillingDate,
  formatEurCents,
  hasLiveStripeSubscription,
  nonRenewingPlanEnd,
  planDisplayName,
  tierKeyFromPlan,
  tierPriceCents,
} from '@/lib/billingAccess';
import { forgetDeactivatedUnits, readDeactivatedUnits, rememberDeactivatedUnits } from '@/lib/flowDeactivatedUnits';
import { setUnitsActive, UnitSurplusPicker, UnitsLoadState, useActiveUnits } from './DowngradeModal';
import { ReEnableUnitsOffer } from './ReEnableUnitsOffer';
import { switchUnitsBackOn } from './reEnableUnits';
import { billingErrorText } from './billingMessages';

export interface PlanChangeRequest {
  tier: PricingTierId;
  interval: 'month' | 'year';
}

interface PlanChangeFlowProps {
  request: PlanChangeRequest | null;
  billing: BillingInfo | null | undefined;
  /** Where Stripe Checkout returns when the host cancels it. */
  returnTo?: 'billing' | 'pricing';
  onClose: () => void;
  /** A change was made (or scheduled) — refresh what the page shows. */
  onChanged?: () => void;
}

/** A preview older than this is recalculated before charging. */
export const PREVIEW_MAX_AGE_MS = 50 * 60 * 1000;
/** Units switched back on after a failed change: all / some / none. */
type RestoreOutcome = 'all' | 'partial' | 'none';

/**
 * Commit errors after which nobody knows whether the change went through
 * (the request was sent; the answer got lost or the server broke midway).
 */
const UNKNOWN_OUTCOME_CODES = new Set(['TIMEOUT', 'NETWORK', 'RELAY', 'SERVER']);

/** Reactivation errors a retry can't fix (Stripe key mix-up, deletion pending). */
const NO_RETRY_CODES = new Set(['BILLING_UNAVAILABLE', 'ACCOUNT_PENDING_DELETION']);

const surplusOf = (r: { activeUnits?: number; maxUnits?: number }): number | null =>
  typeof r.activeUnits === 'number' && typeof r.maxUnits === 'number' ? Math.max(0, r.activeUnits - r.maxUnits) : null;

type DoneResult = Extract<CheckoutResult, { kind: 'updated' | 'scheduled' | 'requires_action' | 'change_cancelled' }>;

type Step =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'confirm_new'; amount: number }
  | { kind: 'confirm_change'; preview: ChangePreview; previewedAt: number; refreshed: boolean }
  | { kind: 'confirm_units'; maxUnits: number; preview: PartialPreview | null; serverActive: number | null }
  | { kind: 'submitting' }
  | { kind: 'done'; result: DoneResult }
  | {
      kind: 'error';
      code: string;
      unitsRestored?: RestoreOutcome;
      hostedInvoiceUrl?: string | null;
      /** Units switched off for this change are still off — offer to switch them on. */
      offerReEnable?: boolean;
      /** REACTIVATE_FIRST: the server's end date (cancel_at may differ from the period end). */
      endsAt?: string | null;
      /** REACTIVATE_FIRST before a smaller plan: units will have to go next. */
      unitsNote?: UnitsNote;
      /** Unknown outcome: units switched off for this change stay off. */
      unitsLeftOff?: boolean;
    };

/** How many units the new plan will need switched off (count null = not known). */
type UnitsNote = { count: number | null };

/**
 * REACTIVATE_FIRST (also TOO_MANY_UNITS with reactivate_first): the host
 * turned renewal off; a plan change needs renewal on first (nothing is
 * charged). The button turns it on via the reactivate mutation, then the flow
 * previews again by itself — only then, if the plan is smaller, the unit list
 * appears (never before renewal is on). needs_checkout (the subscription had
 * already ended) re-previews too: it becomes a normal purchase. PAYMENT_ISSUE
 * offers the open invoice / card update right here.
 */
function ReactivateFirst({
  periodEnd,
  lang,
  unitsNote,
  getRun,
  onDone,
  onOpenPortal,
  portalPending,
}: {
  periodEnd: string | null | undefined;
  lang: string;
  unitsNote?: UnitsNote;
  /** The flow's run counter — a click whose run was closed/restarted is ignored. */
  getRun: () => number;
  onDone: () => void;
  onOpenPortal: () => void;
  portalPending: boolean;
}) {
  const { t } = useTranslation();
  const reactivate = useReactivateSubscription();
  const [failed, setFailed] = useState<{ code: string; invoiceUrl: string | null } | null>(null);
  const run = async () => {
    const token = getRun();
    setFailed(null);
    try {
      const res = await reactivate.mutateAsync();
      if (getRun() !== token) return; // the host closed the dialog meanwhile
      if (res.reactivated || res.needsCheckout) onDone();
      else setFailed({ code: 'SERVER', invoiceUrl: null });
    } catch (e) {
      if (getRun() !== token) return;
      const err = e as { code?: string; info?: { body?: Record<string, unknown> | null } | null };
      const url = err?.info?.body?.hosted_invoice_url;
      setFailed({ code: err?.code ?? 'SERVER', invoiceUrl: typeof url === 'string' && url ? url : null });
    }
  };
  const date = formatBillingDate(periodEnd, lang);
  return (
    <div className="space-y-3" data-testid="reactivate-first">
      <p className="text-sm">
        {date
          ? t('billing.change.reactivateFirst', {
              date,
              defaultValue: 'Automatska obnova je isključena (pretplata završava {{date}}). Za promjenu plana prvo je uključi — ništa se ne naplaćuje.',
            })
          : t('billing.change.reactivateFirstNoDate', 'Automatska obnova je isključena. Za promjenu plana prvo je uključi — ništa se ne naplaćuje.')}
      </p>
      {unitsNote && (
        <p className="text-sm" data-testid="reactivate-units-note">
          {unitsNote.count && unitsNote.count > 0
            ? t('billing.change.reactivateUnitsNote', {
                count: unitsNote.count,
                defaultValue: 'Za ovaj plan morat ćeš isključiti i {{count}} jedinica — odabrat ćeš ih u sljedećem koraku.',
              })
            : t('billing.change.reactivateUnitsNoteGeneric', 'Za ovaj plan morat ćeš isključiti i dio jedinica — odabrat ćeš ih u sljedećem koraku.')}
        </p>
      )}
      <p className="text-xs text-[hsl(var(--muted-foreground))]">
        {t('billing.change.reactivateUndo', 'Ako zatim odustaneš od promjene, obnovu možeš ponovno isključiti u Naplati.')}
      </p>
      {failed && (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert" data-testid="reactivate-first-error">
          {billingErrorText(t, failed.code)}
        </p>
      )}
      {failed?.code === 'PAYMENT_ISSUE' && (
        <div className="flex flex-col sm:flex-row gap-2">
          {failed.invoiceUrl && (
            <a
              href={failed.invoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reactivate-invoice-link"
              className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2 rounded-lg bg-[hsl(var(--primary))] text-white font-semibold text-sm"
            >
              <ExternalLink className="h-4 w-4" />
              {t('billing.change.payOpenInvoice', 'Plati otvoreni račun')}
            </a>
          )}
          <Button variant="outline" onClick={onOpenPortal} disabled={portalPending} className="w-full sm:w-auto gap-2" data-testid="reactivate-update-card">
            {portalPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
            {t('billing.lifecycle.updateCard', 'Ažuriraj karticu')}
          </Button>
        </div>
      )}
      {!(failed && NO_RETRY_CODES.has(failed.code)) && (
        <Button onClick={run} disabled={reactivate.isPending} className="w-full sm:w-auto gap-2" data-testid="reactivate-first-button">
          {reactivate.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
          {t('billing.change.reactivateFirstButton', 'Uključi automatsku obnovu')}
        </Button>
      )}
    </div>
  );
}

/**
 * The unit list for the "too many units" confirmation. Mounted only in that
 * step (loads the host's units); reports how many must be ticked once the
 * list has loaded (spinner / error + retry until then).
 */
function ActiveUnitsPicker({
  maxUnits,
  serverActive,
  selected,
  onChange,
  onRequiredChange,
}: {
  maxUnits: number;
  /** active_units the server counted — the list must match before picking. */
  serverActive: number | null;
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  onRequiredChange: (needed: { required: number; active: number } | null) => void;
}) {
  const { t } = useTranslation();
  const { units, isLoading, isError, isFetching, isFetchedAfterMount, refetch } = useActiveUnits();
  // Always load the list fresh when this step opens (a cached one may be old),
  // and once more if it still disagrees with the server's count.
  const [refreshedTwice, setRefreshedTwice] = useState(false);
  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const fresh = isFetchedAfterMount && !isFetching;
  const mismatch = serverActive !== null && units.length !== serverActive;
  useEffect(() => {
    if (fresh && mismatch && !refreshedTwice) {
      setRefreshedTwice(true);
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fresh, mismatch, refreshedTwice]);
  const ready = !isLoading && !isError && fresh && (!mismatch || refreshedTwice);
  const toRemove = Math.max(0, units.length - maxUnits);
  useEffect(() => {
    onRequiredChange(ready ? { required: toRemove, active: units.length } : null);
  }, [ready, toRemove, units.length, onRequiredChange]);
  if (!ready) return <UnitsLoadState isLoading={!isError} isError={isError} onRetry={refetch} />;
  if (toRemove === 0) {
    return <p className="text-sm">{t('subscription.downgrade.nothingToRemove', 'Tvoje aktivne jedinice stanu u novi plan — ništa ne treba deaktivirati.')}</p>;
  }
  return <UnitSurplusPicker units={units} toRemove={toRemove} selected={selected} onChange={onChange} />;
}

export function PlanChangeFlow({ request, billing, returnTo = 'billing', onClose, onChanged }: PlanChangeFlowProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'hr' ? 'hr' : i18n.language;
  const queryClient = useQueryClient();
  const createCheckout = useCreateCheckout();
  const portal = useCustomerPortal();
  const [step, setStep] = useState<Step>({ kind: 'idle' });
  const [selectedUnits, setSelectedUnits] = useState<Set<string>>(new Set());
  // Filled by ActiveUnitsPicker once the units loaded: how many must be ticked.
  const [unitsNeeded, setUnitsNeeded] = useState<{ required: number; active: number } | null>(null);
  const handleRequiredChange = useCallback(
    (needed: { required: number; active: number } | null) => setUnitsNeeded(needed),
    [],
  );
  // Units switched off for a change whose amount still has to be shown
  // (re-preview) — switched back on if the host cancels there.
  const awaitingRepreview = useRef<string[]>([]);
  const runId = useRef(0);
  // The Clerk user id keys the remembered units (and the billing query).
  const { user } = useUser();
  const userId = user?.id ?? null;
  // The step, readable from the pageshow handler.
  const stepRef = useRef<Step>(step);
  stepRef.current = step;

  const live = hasLiveStripeSubscription(billing);
  const planName = request ? PRICING_TIERS[request.tier].name : '';
  const intervalLabel = (iv: 'month' | 'year' | null | undefined) =>
    iv === 'month' ? t('billing.monthly', 'mjesečno') : t('billing.yearly', 'godišnje');
  const perInterval = (iv: 'month' | 'year') =>
    iv === 'year' ? t('billing.change.perYear', 'godišnje') : t('billing.change.perMonth', 'mjesečno');
  const money = (cents: number | null | undefined) => formatEurCents(cents ?? 0, lang);
  const date = (iso: string | null | undefined) => formatBillingDate(iso, lang);

  // The request IS the host's current Stripe plan (only sensible with a
  // pending change: "keep my plan" = cancel the scheduled change).
  const currentTier = live ? tierKeyFromPlan(billing?.subscription_plan) : null;
  const currentInterval = billing?.billing_interval === 'month' ? 'month' : 'year';
  const keepsCurrent = !!request && currentTier === request.tier && currentInterval === request.interval;
  const pendingName = planDisplayName(billing?.pending_plan);

  const showUnitsStep = (req: PlanChangeRequest, result: Extract<CheckoutResult, { kind: 'error' }>) => {
    setSelectedUnits(new Set());
    setUnitsNeeded(null);
    setStep({
      kind: 'confirm_units',
      maxUnits: result.maxUnits ?? PRICING_TIERS[req.tier].maxUnits,
      preview: result.preview ?? null,
      serverActive: result.activeUnits ?? null,
    });
  };

  /** Ask the server what would happen (nothing changes yet). */
  const prepare = useCallback(
    async (req: PlanChangeRequest, refreshed = false) => {
      const myRun = ++runId.current;
      setStep({ kind: 'loading' });
      const result = await createCheckout.mutateAsync({ ...req, preview: true, returnTo });
      if (myRun !== runId.current) return; // closed or restarted meanwhile
      if (result.kind === 'preview' && result.preview.checkout) {
        setStep({ kind: 'confirm_new', amount: result.preview.amountDueNow || tierPriceCents(req.tier, req.interval) });
      } else if (result.kind === 'preview') {
        setStep({ kind: 'confirm_change', preview: result.preview, previewedAt: Date.now(), refreshed });
      } else if (result.kind === 'error' && result.code === 'TOO_MANY_UNITS' && result.reactivateFirst) {
        // Renewal is off: turn it on first, THEN pick the units (re-preview).
        setStep({ kind: 'error', code: 'REACTIVATE_FIRST', endsAt: result.currentPeriodEnd, unitsNote: { count: surplusOf(result) } });
      } else if (result.kind === 'error' && result.code === 'TOO_MANY_UNITS') {
        showUnitsStep(req, result);
      } else if (result.kind === 'error') {
        // A preview that timed out sent nothing — not the "maybe charged" case.
        setStep({
          kind: 'error',
          code: result.code === 'TIMEOUT' ? 'TIMEOUT_PREVIEW' : result.code,
          hostedInvoiceUrl: result.hostedInvoiceUrl,
          endsAt: result.currentPeriodEnd,
        });
      } else if (result.kind === 'change_cancelled') {
        setStep({ kind: 'done', result });
      } else {
        setStep({ kind: 'error', code: 'SERVER' });
      }
    },
    // createCheckout.mutateAsync is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [returnTo],
  );

  useEffect(() => {
    if (!request) {
      runId.current++;
      setStep({ kind: 'idle' });
      return;
    }
    void prepare(request);
    // Only a NEW request restarts the flow (not every billing refetch).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  // Unmounted: late answers (reactivation, background check) are ignored.
  useEffect(
    () => () => {
      runId.current++;
    },
    [],
  );

  // Back from Stripe Checkout with the browser's back button: the page comes
  // from the back/forward cache still showing "submitting" — make it closable
  // and offer the units remembered for that checkout.
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (!e.persisted || stepRef.current.kind !== 'submitting') return;
      setStep({ kind: 'error', code: 'CHECKOUT_RETURNED', offerReEnable: readDeactivatedUnits(userId).length > 0 });
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [userId]);

  /** Switch units back on; only the ids that really changed count. */
  const reactivate = async (ids: string[]): Promise<RestoreOutcome> => {
    if (!ids.length) return 'all';
    try {
      const out = await switchUnitsBackOn(userId, ids, queryClient);
      if (out.stillOff.length === 0) return 'all';
      return out.changed.length > 0 ? 'partial' : 'none';
    } catch (e) {
      console.error('Re-activating units failed:', e);
      return 'none';
    }
  };


  const close = () => {
    runId.current++;
    // Switched off for a change the host then didn't confirm → back on.
    if (awaitingRepreview.current.length) {
      void reactivate(awaitingRepreview.current);
      awaitingRepreview.current = [];
    }
    setStep({ kind: 'idle' });
    setSelectedUnits(new Set());
    onClose();
  };

  /** The real create-checkout call + what to show for its answer. */
  const commit = async (req: PlanChangeRequest, prorationDate: number | null, deactivated: string[]) => {
    const result = await createCheckout.mutateAsync({
      ...req,
      returnTo,
      ...(prorationDate !== null ? { proration_date: prorationDate } : {}),
    });
    if (result.kind === 'redirect') {
      // Leaving for Stripe: switched-off units stay remembered (?cancelled
      // on return offers to switch them back on).
      window.location.href = result.url;
      return; // stay on "submitting" while the browser leaves
    }
    if (result.kind === 'updated' || result.kind === 'scheduled' || result.kind === 'requires_action' || result.kind === 'change_cancelled') {
      awaitingRepreview.current = [];
      // The smaller plan is scheduled: the units stay off on purpose.
      if (result.kind === 'scheduled') forgetDeactivatedUnits(userId);
      setStep({ kind: 'done', result });
      onChanged?.();
      return;
    }
    if (result.kind !== 'error' || UNKNOWN_OUTCOME_CODES.has(result.code)) {
      // Unknown outcome: the change may have gone through (or still go
      // through — the server can finish after we gave up). Closable error
      // that says to check Billing; switched-off units stay off and are not
      // offered back. The page is refreshed (server re-reads Stripe first).
      awaitingRepreview.current = [];
      setStep({
        kind: 'error',
        code: result.kind === 'error' && result.code === 'TIMEOUT' ? 'TIMEOUT' : 'COMMIT_UNKNOWN',
        unitsLeftOff: deactivated.length > 0,
      });
      onChanged?.();
      if (userId) {
        void Promise.resolve()
          .then(() => syncMyBilling(userId))
          .catch(() => false)
          .then(() => queryClient.invalidateQueries({ queryKey: subscriptionKeys.all }));
      }
      return;
    }
    // Failed: switch back on what we switched off for this change.
    let unitsRestored: RestoreOutcome | undefined;
    if (deactivated.length) {
      unitsRestored = await reactivate(deactivated);
      awaitingRepreview.current = [];
    }
    if (
      result.kind === 'error' &&
      (result.code === 'REACTIVATE_FIRST' || (result.code === 'TOO_MANY_UNITS' && result.reactivateFirst))
    ) {
      setStep({
        kind: 'error',
        code: 'REACTIVATE_FIRST',
        endsAt: result.currentPeriodEnd,
        unitsRestored,
        unitsNote: deactivated.length
          ? { count: null }
          : result.code === 'TOO_MANY_UNITS'
            ? { count: surplusOf(result) }
            : undefined,
      });
      return;
    }
    if (result.kind === 'error' && result.code === 'TOO_MANY_UNITS' && !deactivated.length) {
      showUnitsStep(req, result);
      return;
    }
    setStep({
      kind: 'error',
      code: result.code,
      unitsRestored,
      hostedInvoiceUrl: result.hostedInvoiceUrl,
      endsAt: result.currentPeriodEnd,
    });
  };

  const submit = async () => {
    if (!request) return;
    if (step.kind === 'confirm_change') {
      // An old preview may no longer match what Stripe would charge.
      if (Date.now() - step.previewedAt > PREVIEW_MAX_AGE_MS) {
        await prepare(request, true);
        return;
      }
      const prorationDate = step.preview.effective === 'now' ? step.preview.prorationDate : null;
      const deactivated = awaitingRepreview.current;
      setStep({ kind: 'submitting' });
      await commit(request, prorationDate, deactivated);
      return;
    }
    if (step.kind === 'confirm_units') {
      const ids = Array.from(selectedUnits);
      const p = step.preview;
      setStep({ kind: 'submitting' });
      try {
        await setUnitsActive(ids, false, queryClient);
        rememberDeactivatedUnits(userId, ids);
      } catch (e) {
        console.error('Deactivating units failed:', e);
        setStep({ kind: 'error', code: 'DEACTIVATE_FAILED' });
        return;
      }
      // A charge "now" whose amount the server couldn't tell yet: show it
      // first (re-preview); cancelling there switches the units back on.
      if (p && p.effective === 'now' && p.amountDueNow === null) {
        awaitingRepreview.current = ids;
        await prepare(request);
        return;
      }
      await commit(request, null, ids);
      return;
    }
    setStep({ kind: 'submitting' });
    await commit(request, null, []);
  };

  const openPortal = async () => {
    try {
      const res = await portal.mutateAsync();
      window.location.href = res.url;
    } catch (e) {
      setStep({ kind: 'error', code: (e as { code?: string })?.code ?? 'SERVER' });
    }
  };

  if (!request || step.kind === 'idle') return null;

  // Replacing a gift / beta / trial with a paid plan (paid plan wins at once).
  const replacedEnd = !live && effectiveHasAccess(billing) ? nonRenewingPlanEnd(billing) : null;
  const replacedSource = billing?.plan_source;
  const capOfNew = PRICING_TIERS[request.tier].maxUnits;

  let body: React.ReactNode = null;
  let footer: React.ReactNode = null;

  const summaryRow = (label: string, value: React.ReactNode, testId?: string) => (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-[hsl(var(--border))] last:border-0">
      <span className="text-sm text-[hsl(var(--muted-foreground))]">{label}</span>
      <span className="text-sm font-semibold text-right" data-testid={testId}>{value}</span>
    </div>
  );

  const replacedWarning = replacedEnd ? (
    <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 text-sm text-amber-800 dark:text-amber-200">
      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
      <span>
        {t(replacedSource === 'trial' ? 'billing.change.replacesTrial' : 'billing.change.replacesGift', {
          date: date(replacedEnd),
          defaultValue: 'Tvoj besplatni plan vrijedi do {{date}}. Ako sada kupiš plan, naplata kreće odmah i plaćeni plan ga zamjenjuje.',
        })}
      </span>
    </div>
  ) : null;

  const cancelsPendingNote = (
    <p className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="cancels-pending">
      {pendingName
        ? t('billing.change.cancelsPending', { plan: pendingName, defaultValue: 'Zakazana promjena na {{plan}} se poništava.' })
        : t('billing.change.cancelsPendingGeneric', 'Zakazana promjena plana se poništava.')}
    </p>
  );
  const clearsCancellationNote = (
    <p className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="clears-cancellation">
      {t('billing.change.clearsCancellation', 'Automatska obnova time se ponovno uključuje.')}
    </p>
  );

  const newPurchaseSummary = (amount: number) => (
    <div className="rounded-lg border border-[hsl(var(--border))] px-3">
      {summaryRow(t('billing.change.plan', 'Plan'), `${planName}, ${intervalLabel(request.interval)}`)}
      {summaryRow(t('billing.change.chargedToday', 'Danas se naplaćuje'), money(amount), 'charge-today')}
      {summaryRow(
        t('billing.change.renewal', 'Obnova'),
        request.interval === 'year'
          ? t('billing.change.renewsYearly', 'svake godine, dok je ne isključiš')
          : t('billing.change.renewsMonthly', 'svaki mjesec, dok je ne isključiš'),
      )}
      {summaryRow(t('billing.change.units', 'Jedinice'), t('billing.change.upToUnits', { count: capOfNew, defaultValue: 'do {{count}}' }))}
    </div>
  );

  // After a failed change: did the units switched off for it come back on?
  // Some still off → the offer below lets the host try again.
  const restoredBlock = (s: Extract<Step, { kind: 'error' }>) => (
    <>
      {s.unitsRestored === 'all' && (
        <p className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="units-restored">
          {t('billing.change.unitsRestored', 'Jedinice koje si označio ponovno su aktivne.')}
        </p>
      )}
      {s.unitsRestored === 'partial' && (
        <p className="text-sm text-amber-700 dark:text-amber-300" data-testid="units-not-restored" data-result="partial">
          {t('billing.change.unitsPartlyRestored', 'Dio označenih jedinica ostao je deaktiviran — možeš ih ponovno uključiti ovdje.')}
        </p>
      )}
      {s.unitsRestored === 'none' && (
        <p className="text-sm text-amber-700 dark:text-amber-300" data-testid="units-not-restored" data-result="none">
          {t('billing.change.unitsNotRestored', 'Označene jedinice ostale su deaktivirane — možeš ih ponovno uključiti ovdje ili na stranici jedinice.')}
        </p>
      )}
      {(s.offerReEnable || s.unitsRestored === 'partial' || s.unitsRestored === 'none') && <ReEnableUnitsOffer billing={billing} />}
    </>
  );

  if (step.kind === 'loading' || step.kind === 'submitting') {
    body = (
      <div className="py-10 flex flex-col items-center gap-3 text-[hsl(var(--muted-foreground))]">
        <Loader2 className="h-6 w-6 animate-spin" />
        <p className="text-sm">
          {step.kind === 'loading'
            ? t('billing.change.calculating', 'Računam točan iznos…')
            : t('billing.change.working', 'Samo trenutak…')}
        </p>
      </div>
    );
  } else if (step.kind === 'confirm_new') {
    body = (
      <div className="space-y-4">
        <p className="text-sm">
          {t('billing.change.newIntro', {
            plan: planName,
            amount: money(step.amount),
            defaultValue: 'Danas se naplaćuje {{amount}} za plan {{plan}}. Plaćaš karticom na sigurnoj Stripe stranici.',
          })}
        </p>
        {newPurchaseSummary(step.amount)}
        {replacedWarning}
      </div>
    );
    footer = (
      <Button onClick={submit} className="w-full sm:w-auto gap-2" size="lg">
        <CreditCard className="h-4 w-4" />
        {t('billing.change.toPayment', 'Nastavi na plaćanje')}
      </Button>
    );
  } else if (step.kind === 'confirm_units') {
    // What the server will do once the units fit — from its own answer
    // (catalogue values only when an older server sent none). The server sends
    // TOO_MANY_UNITS "now" only for a new purchase; a change of a live plan to
    // a smaller one is always from the renewal (period_end).
    const p = step.preview;
    const isNewPurchase = p ? p.checkout || p.effective === 'now' : !live;
    const newPrice = p?.newPrice ?? tierPriceCents(request.tier, request.interval);
    const todayAmount = p ? (p.effective === 'period_end' ? 0 : p.amountDueNow) : isNewPurchase ? newPrice : 0;
    const startsOn = p ? (p.effective === 'period_end' ? p.periodEnd : null) : live ? billing?.current_period_end ?? null : null;
    const toRemove = unitsNeeded?.required ?? Number.POSITIVE_INFINITY;
    body = (
      <div className="space-y-4">
        <p className="text-sm">
          {t('billing.change.unitsIntro', {
            plan: planName,
            max: step.maxUnits,
            current: unitsNeeded?.active ?? billing?.current_units ?? 0,
            defaultValue: 'Plan {{plan}} dopušta {{max}} aktivnih jedinica, a ti ih imaš {{current}}. Označi koje će se deaktivirati — to se događa tek kad potvrdiš.',
          })}
        </p>
        <div className="rounded-lg border border-[hsl(var(--border))] px-3" data-testid="units-step-summary">
          {summaryRow(isNewPurchase ? t('billing.change.plan', 'Plan') : t('billing.change.newPlan', 'Novi plan'), `${planName}, ${intervalLabel(request.interval)}`)}
          {summaryRow(
            t('billing.change.chargedToday', 'Danas se naplaćuje'),
            todayAmount === null ? t('billing.change.amountAfterUnits', 'iznos vidiš prije potvrde') : money(todayAmount),
            'charge-today',
          )}
          {startsOn && summaryRow(t('billing.change.startsOn', 'Vrijedi od'), date(startsOn), 'starts-on')}
          {summaryRow(
            isNewPurchase ? t('billing.change.renewal', 'Obnova') : t('billing.change.thenPrice', 'Nakon toga'),
            `${money(newPrice)} ${perInterval(request.interval)}`,
            'new-price',
          )}
        </div>
        {p?.cancelsPendingChange && cancelsPendingNote}
        {p?.clearsCancellation && clearsCancellationNote}
        <ActiveUnitsPicker
          maxUnits={step.maxUnits}
          serverActive={step.serverActive}
          selected={selectedUnits}
          onChange={setSelectedUnits}
          onRequiredChange={handleRequiredChange}
        />
        <p className="text-xs text-[hsl(var(--muted-foreground))]">{t('subscription.downgrade.confirmBody')}</p>
        {isNewPurchase && (
          <p className="text-xs text-[hsl(var(--muted-foreground))]" data-testid="units-checkout-note">
            {t('billing.change.unitsCheckoutNote', 'Ako odustaneš od plaćanja, jedinice možeš ponovno uključiti na stranici svake jedinice.')}
          </p>
        )}
        {isNewPurchase && replacedWarning}
      </div>
    );
    footer = (
      <Button
        onClick={submit}
        disabled={unitsNeeded === null || selectedUnits.size < toRemove}
        className="w-full sm:w-auto gap-2"
        size="lg"
        data-testid="confirm-units"
      >
        <CheckCircle className="h-4 w-4" />
        {isNewPurchase
          ? t('billing.change.deactivateAndPay', 'Deaktiviraj označene i nastavi na plaćanje')
          : t('billing.change.deactivateAndSchedule', 'Deaktiviraj označene i zakaži promjenu')}
      </Button>
    );
  } else if (step.kind === 'confirm_change') {
    const p = step.preview;
    const now = p.effective === 'now';
    const monthToYear = currentInterval === 'month' && request.interval === 'year';
    const headline = keepsCurrent
      ? t('billing.change.keepIntro', {
          plan: planName,
          defaultValue: 'Zadržavaš plan {{plan}} — zakazana promjena se poništava. Danas se ništa ne naplaćuje.',
        })
      : now
        ? p.amountDueNow > 0
          ? monthToYear
            ? t('billing.change.nowIntroYear', {
                plan: planName,
                amount: money(p.amountDueNow),
                defaultValue: 'Prelaziš na {{plan}} uz godišnju naplatu odmah. Danas se naplaćuje {{amount}} — godišnja cijena umanjena za neiskorišteni dio tekućeg mjeseca.',
              })
            : t('billing.change.nowIntro', {
                plan: planName,
                amount: money(p.amountDueNow),
                defaultValue: 'Prelaziš na {{plan}} odmah. Danas se naplaćuje {{amount}} — razlika za ostatak tekućeg razdoblja.',
              })
          : t('billing.change.nowIntroFree', {
              plan: planName,
              defaultValue: 'Prelaziš na {{plan}} odmah. Danas se ništa ne naplaćuje.',
            })
        : t('billing.change.laterIntro', {
            plan: planName,
            date: date(p.periodEnd),
            defaultValue: 'Od {{date}} prelaziš na {{plan}}. Danas se ništa ne naplaćuje — do tada zadržavaš sadašnji plan.',
          });
    body = (
      <div className="space-y-4">
        {step.refreshed && (
          <p className="text-sm text-amber-700 dark:text-amber-300" data-testid="preview-refreshed">
            {t('billing.change.recalculated', 'Iznos je ponovno izračunat jer je prošlo neko vrijeme — provjeri ga i potvrdi.')}
          </p>
        )}
        <p className="text-sm" data-testid="change-headline">{headline}</p>
        <div className="rounded-lg border border-[hsl(var(--border))] px-3">
          {summaryRow(t('billing.change.newPlan', 'Novi plan'), `${planName}, ${intervalLabel(request.interval)}`)}
          {summaryRow(t('billing.change.chargedToday', 'Danas se naplaćuje'), money(now ? p.amountDueNow : 0), 'charge-today')}
          {!now && summaryRow(t('billing.change.startsOn', 'Vrijedi od'), date(p.periodEnd) || '—', 'starts-on')}
          {summaryRow(
            t('billing.change.thenPrice', 'Nakon toga'),
            `${money(p.newPrice)} ${perInterval(request.interval)}`,
            'new-price',
          )}
          {now && p.periodEnd && summaryRow(t('billing.change.nextCharge', 'Sljedeća naplata'), date(p.periodEnd), 'next-charge')}
        </div>
        {p.cancelsPendingChange && !keepsCurrent && cancelsPendingNote}
        {p.clearsCancellation && clearsCancellationNote}
      </div>
    );
    footer = (
      <Button onClick={submit} className="w-full sm:w-auto gap-2" size="lg" data-testid="confirm-change">
        <CheckCircle className="h-4 w-4" />
        {keepsCurrent
          ? t('billing.pending.cancel', 'Odustani od promjene')
          : now
            ? p.amountDueNow > 0
              ? t('billing.change.confirmPay', { amount: money(p.amountDueNow), defaultValue: 'Potvrdi i plati {{amount}}' })
              : t('billing.change.confirm', 'Potvrdi promjenu')
            : t('billing.change.confirmSchedule', 'Zakaži promjenu')}
      </Button>
    );
  } else if (step.kind === 'done') {
    const r = step.result;
    if (r.kind === 'requires_action') {
      body = (
        <div className="space-y-4">
          <p className="text-sm">
            {t('billing.change.requiresAction', 'Tvoja banka traži dodatnu potvrdu plaćanja. Otvori Stripe stranicu i potvrdi plaćanje — plan se zatim mijenja sam (može potrajati minutu).')}
          </p>
          {r.hostedInvoiceUrl ? (
            <a
              href={r.hostedInvoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="confirm-payment-link"
              className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-3 rounded-lg bg-[hsl(var(--primary))] text-white font-semibold"
            >
              <ExternalLink className="h-4 w-4" />
              {t('billing.change.confirmPayment', 'Potvrdi plaćanje')}
            </a>
          ) : (
            <Button onClick={openPortal} disabled={portal.isPending} className="w-full sm:w-auto gap-2">
              {portal.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
              {t('billing.change.openStripe', 'Otvori Stripe (kartica i računi)')}
            </Button>
          )}
        </div>
      );
    } else {
      const text =
        r.kind === 'change_cancelled'
          ? t('billing.pending.cancelled', 'Zakazana promjena je poništena. Ostaje ti sadašnji plan.')
          : r.kind === 'scheduled'
            ? t('billing.change.scheduledDone', {
                plan: planDisplayName(r.tierId) ?? planName,
                date: date(r.effectiveAt),
                interval: intervalLabel(r.interval ?? request.interval),
                defaultValue: 'Promjena je zakazana: od {{date}} prelaziš na {{plan}} ({{interval}}). Do tada ostaje sadašnji plan.',
              })
            : r.amountCharged && r.amountCharged > 0
              ? t('billing.change.updatedCharged', {
                  plan: planDisplayName(r.tierId) ?? planName,
                  amount: money(r.amountCharged),
                  defaultValue: 'Gotovo — sada imaš plan {{plan}}. Naplaćeno: {{amount}}.',
                })
              : t('billing.change.updated', {
                  plan: planDisplayName(r.tierId) ?? planName,
                  defaultValue: 'Gotovo — sada imaš plan {{plan}}.',
                });
      body = (
        <div className="space-y-3">
          <div className="flex items-start gap-3" data-testid="change-done" data-result={r.kind}>
            <CheckCircle className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
            <p className="text-sm">{text}</p>
          </div>
          {/* The smaller plan was dropped → units switched off for it can come back. */}
          {r.kind === 'change_cancelled' && <ReEnableUnitsOffer billing={billing} />}
        </div>
      );
    }
    footer = (
      <Button onClick={close} className="w-full sm:w-auto">
        {t('common.close', 'Zatvori')}
      </Button>
    );
  } else if (step.kind === 'error' && step.code === 'REACTIVATE_FIRST') {
    body = (
      <div className="space-y-3">
        {restoredBlock(step)}
        <ReactivateFirst
          periodEnd={step.endsAt ?? billing?.current_period_end}
          lang={lang}
          unitsNote={step.unitsNote}
          getRun={() => runId.current}
          onDone={() => void prepare(request)}
          onOpenPortal={openPortal}
          portalPending={portal.isPending}
        />
      </div>
    );
  } else if (step.kind === 'error') {
    const invoiceUrl = step.hostedInvoiceUrl;
    const errorText =
      step.code === 'DEACTIVATE_FAILED'
        ? t('subscription.downgrade.failed', 'Deaktivacija nije uspjela. Ništa nije promijenjeno na pretplati — pokušaj ponovno.')
        : step.code === 'TIMEOUT_PREVIEW'
          ? t('billing.errors.timeoutPreview', 'Izračun iznosa nije stigao na vrijeme. Ništa nije poslano ni naplaćeno — pokušaj ponovno.')
          : step.code === 'CHECKOUT_RETURNED'
            ? t('billing.change.checkoutReturned', 'Vratio si se sa stranice za plaćanje. Ako plaćanje nisi dovršio, ništa nije naplaćeno.')
            : step.code === 'COMMIT_UNKNOWN'
              ? t('billing.errors.commitUnknown', 'Veza se prekinula dok se promjena slala. Prije novog pokušaja provjeri u Naplati je li se plan već promijenio, da ne platiš dvaput.')
              : billingErrorText(t, step.code);
    body = (
      <div className="space-y-2" role="alert">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <p className="text-sm" data-testid="change-error">{errorText}</p>
        </div>
        {step.unitsLeftOff && (
          <p className="text-sm text-amber-700 dark:text-amber-300" data-testid="units-left-off">
            {t('billing.change.unitsLeftOff', 'Jedinice koje si označio ostaju isključene. Ako se plan nije promijenio, uključi ih na stranici svake jedinice.')}
          </p>
        )}
        {restoredBlock(step)}
      </div>
    );
    const invoiceLink = invoiceUrl ? (
      <a
        href={invoiceUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="open-invoice-link"
        className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2 rounded-lg bg-[hsl(var(--primary))] text-white font-semibold text-sm"
      >
        <ExternalLink className="h-4 w-4" />
        {step.code === 'PAYMENT_PENDING'
          ? t('billing.change.openInvoice', 'Otvori račun i potvrdi plaćanje')
          : t('billing.change.payOpenInvoice', 'Plati otvoreni račun')}
      </a>
    ) : null;
    footer =
      step.code === 'PAYMENT_ISSUE' ? (
        <>
          {invoiceLink}
          <Button onClick={openPortal} disabled={portal.isPending} variant={invoiceLink ? 'outline' : 'default'} className="w-full sm:w-auto gap-2">
            {portal.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
            {t('billing.lifecycle.updateCard', 'Ažuriraj karticu')}
          </Button>
        </>
      ) : step.code === 'PAYMENT_PENDING' ? (
        invoiceLink
      ) : null;
  }

  const title =
    step.kind === 'confirm_new' ||
    (step.kind === 'confirm_units' && (step.preview ? step.preview.checkout || step.preview.effective === 'now' : !live))
      ? t('billing.change.titleNew', { plan: planName, defaultValue: 'Kupnja plana {{plan}}' })
      : t('billing.change.title', { plan: planName, defaultValue: 'Promjena plana: {{plan}}' });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="plan-change-title"
      data-testid="plan-change-dialog"
      data-step={step.kind}
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
    >
      <div className="w-full sm:max-w-lg bg-[hsl(var(--background))] rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-center justify-between gap-3 p-5 border-b border-[hsl(var(--border))]">
          <h2 id="plan-change-title" className="text-lg font-semibold">{title}</h2>
          <button
            type="button"
            onClick={close}
            disabled={step.kind === 'submitting'}
            className="p-1 rounded hover:bg-[hsl(var(--muted))] disabled:opacity-40"
            aria-label={t('common.close', 'Zatvori')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-5">{body}</div>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 p-5 pt-0">
          {step.kind !== 'done' && step.kind !== 'submitting' && (
            <Button variant="outline" onClick={close} className="w-full sm:w-auto">
              {t('common.cancel', 'Odustani')}
            </Button>
          )}
          {footer}
        </div>
      </div>
    </div>
  );
}

export default PlanChangeFlow;
